/**
 * In-app organization switcher.
 *
 * Two exports, matching the RoleManagementPanel split:
 *
 * - `OrgSwitcher` — the PRESENTATIONAL control. Props-driven so the functional
 *   tier renders every shape (one org → nothing to switch, many orgs → a picker)
 *   from literal data, no backend.
 * - `OrgSwitcherSection` — the fetching container. Loads the acting identity's
 *   memberships (`sdk.auth.listOrganizations`) and on choice mints a fresh
 *   org-scoped token via `switchOrganizationAction`. Account-scoped client state
 *   is reset by the teardown registry at the single writer (app-shell ADR-001),
 *   never here.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import type { OrgMembership } from '@localagent/sdk';
import {
  claimsAtom,
  organizationsAtom,
  switchOrganizationAction,
} from '../stores/auth/atoms';
import { useOrganizations } from '../hooks/useAuthManagement';
import { hydrateStytchSession } from '../auth/hydrateStytchSession';
import { getRegisteredStytchClient } from '../auth/StytchSessionAuthProvider';

export interface OrgSwitcherProps {
  organizations: OrgMembership[];
  currentOrgId: string | null;
  isSwitching?: boolean;
  error?: string | null;
  onSwitch: (organizationId: string) => void;
}

export function OrgSwitcher({
  organizations,
  currentOrgId,
  isSwitching = false,
  error = null,
  onSwitch,
}: OrgSwitcherProps) {
  // Nothing to switch between — a single-membership identity never sees a picker.
  if (organizations.length <= 1) {
    return null;
  }

  return (
    <div className="space-y-2" data-testid="org-switcher">
      <label
        htmlFor="org-switcher-select"
        className="text-sm font-medium text-muted-foreground"
      >
        Organization
      </label>
      <select
        id="org-switcher-select"
        data-testid="org-switcher-select"
        value={currentOrgId ?? ''}
        disabled={isSwitching}
        onChange={(e) => {
          const next = e.target.value;
          if (next && next !== currentOrgId) onSwitch(next);
        }}
        className="w-full rounded bg-muted px-3 py-2 text-sm disabled:opacity-50"
      >
        {organizations.map((org) => (
          <option key={org.organization_id} value={org.organization_id}>
            {org.name || org.organization_id}
          </option>
        ))}
      </select>
      {error && (
        <p className="text-sm text-destructive" data-testid="org-switcher-error">
          {error}
        </p>
      )}
    </div>
  );
}

export function OrgSwitcherSection() {
  const claims = useAtomValue(claimsAtom);
  const setOrganizations = useSetAtom(organizationsAtom);
  const switchOrg = useSetAtom(switchOrganizationAction);
  const orgsQuery = useOrganizations();

  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Identity-change teardown clears the react-query cache (app-shell ADR-001).
  // Keep the last multi-org list so the picker does not unmount mid-switch.
  const lastMemberships = useRef<OrgMembership[]>([]);
  if ((orgsQuery.data?.length ?? 0) > 1) {
    lastMemberships.current = orgsQuery.data!;
  }
  const organizations =
    (orgsQuery.data?.length ?? 0) > 1 ? orgsQuery.data! : lastMemberships.current;

  // Mirror the fetched memberships into the atom so the rest of the shell can
  // read "how many orgs does this identity have" without re-fetching.
  useEffect(() => {
    if (organizations.length > 0) setOrganizations(organizations);
  }, [organizations, setOrganizations]);

  const handleSwitch = useCallback(
    async (organizationId: string) => {
      setError(null);
      setIsSwitching(true);
      try {
        // Desktop has no StytchB2BProvider. Hydrate only when the web
        // client is registered; the daemon already holds the new bearer.
        await switchOrg(organizationId, async (tokens) => {
          const stytch = getRegisteredStytchClient();
          // Identity already moved in switchOrganizationAction. Do not pin
          // isSwitching on a hung session.authenticate() (app-shell ADR-001).
          if (stytch) void hydrateStytchSession(stytch, tokens);
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to switch organization';
        // An exchange that returns IST / needs MFA lands
        // on `/authenticate` for the second factor; `step=mfa` declares that
        // entry so the route's held-identity redirect stands aside.
        if (/ADDITIONAL_AUTH_REQUIRED/i.test(message)) {
          window.location.assign('/authenticate?step=mfa');
          return;
        }
        setError(message);
      } finally {
        setIsSwitching(false);
      }
    },
    [switchOrg],
  );

  if (organizations.length <= 1) {
    return null;
  }

  return (
    <OrgSwitcher
      organizations={organizations}
      currentOrgId={claims?.orgId ?? null}
      isSwitching={isSwitching}
      error={error}
      onSwitch={(orgId) => void handleSwitch(orgId)}
    />
  );
}
